import React, {FC, useMemo} from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import SelectInput from '@geomatico/geocomponents/Forms/SelectInput';

import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import {SxProps} from '@mui/system/styleFunctionSx/styleFunctionSx';
import {FILTER_KEY, MAB_TYPES, PROTECTION_TYPES} from '@/config';
import {useTranslation} from 'react-i18next';
import {Crop, FilterGroup, Municipality, Use} from '@/domain/services/Clas';
import {Profile} from '@/domain/entities/Profile';
import {toSelectInputOption} from '@/domain/useCases/toSelectInputOption';
import {Accordion, AccordionDetails, AccordionSummary} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {capitalizeWords} from '@/utils/capitalizeWords';

export type Props = {
  isAdmin: boolean,
  isExpanded: boolean,
  availableYears?: Array<number>,
  crops?: Array<Crop>,
  municipalities?: Array<Municipality>,
  users?: Array<Profile>,
  uses?: Array<Use>,
  enpTypes?: Array<string>,
  rnTypes?: Array<string>,
  mabNames?: Array<string>,
  year?: number,
  filterGroup: FilterGroup,
  onYearChange: (year: number) => void,
  onFilterChange: (filterGroup: FilterGroup) => void
  onExpandedChange: (expanded: boolean) => void
}

const FilterPanel: FC<Props> = ({
  isAdmin,
  isExpanded,
  availableYears,
  crops,
  municipalities,
  users,
  uses,
  enpTypes,
  rnTypes,
  year,
  filterGroup,
  onYearChange,
  onFilterChange,
  onExpandedChange
}) => {

  const {t} = useTranslation();

  const selectedUserUuid = useMemo(() =>
    users && users.find(user => user.uuid === filterGroup.userUuid)
  , [users, filterGroup]);
  

  const createInputSx = (filterValue: boolean): SxProps => ({
    ...genericInputSx,
    '& .SelectInput-select': {
      bgcolor: filterValue ? 'primary.main' : undefined,
      color: filterValue ? 'common.white' : undefined,
      fontWeight: filterValue ? 900 : undefined,
    },
    '& .MuiSvgIcon-root': {
      color: filterValue ? 'common.white' : undefined
    },
  });

  const sxProps = {
    availableYears: createInputSx(!!year),
    municipalityUuid: createInputSx(!!filterGroup.municipalityUuid),
    cropUuid: createInputSx(!!filterGroup.cropUuid),
    useUuid: createInputSx(!!filterGroup.useUuid),
    protection_type: createInputSx(!!filterGroup.protectionType),
    enpTypes: createInputSx(!!filterGroup.enpType),
    rnTypes: createInputSx(!!filterGroup.rnType),
    isMab: createInputSx(!!filterGroup.isMab)
  };

  const handleFilterChange = (key: string, value: number | string | undefined) => {
    onFilterChange({
      ...filterGroup,
      [key]: value
    });
  };

  const handleDeleteFilters = () => onFilterChange({});

  const autocompleteSx: SxProps = {
    '& input::placeholder': {
      color: 'rgb(37, 36 36)',
      opacity: 1,
    },
    backgroundColor: selectedUserUuid ? 'primary.main' : undefined,
    '& input': {
      color: selectedUserUuid ? 'white' : undefined, // Aplica color al texto seleccionado
      textTransform: selectedUserUuid ? 'uppercase' : 'none', // Convierte el texto a mayúsculas
      fontWeight: selectedUserUuid ? 'bolder' : undefined,
    },
  };

  const PROPS_INCLUDES_IN_SEARCH = ['first_name', 'last_name', 'nif'];

  return <Accordion sx={accordionSx} expanded={isExpanded}>
    <AccordionSummary expandIcon={<ExpandMoreIcon onClick={() => onExpandedChange(!isExpanded)}/>}>
      <Box display='flex' alignItems='flex-start' justifyContent='space-between'>
        <Typography variant='overline'>{t('actions.filters')}</Typography>
        <IconButton disabled={Object.keys(filterGroup).length === 0} color='primary' onClick={handleDeleteFilters} sx={{fontSize: '1rem', p: 0}}>
          <FilterAltOffIcon/>
        </IconButton>
      </Box>
    </AccordionSummary>
    <AccordionDetails>
      <Box sx={containerSx}>
        {
          isAdmin &&
        <Autocomplete
          size='small'
          options={users ?? []}
          value={selectedUserUuid || null}
          onChange={(e, user) => handleFilterChange(FILTER_KEY.USER_UUID, user?.uuid)}
          renderInput={(params) => 
            <TextField placeholder='Usuario' sx={autocompleteSx} {...params}/>
          }
          getOptionLabel={(option) => option.last_name && option.first_name
            ? `${option.last_name}, ${option.first_name}`
            : option.last_name || option.first_name
          }
          renderOption={(props, option) => (
            <li {...props} key={option.uuid}>
              <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                <Typography>
                  {option.last_name ? `${capitalizeWords(option.last_name)}, ${capitalizeWords(option.first_name)}` : capitalizeWords(option.first_name)}
                </Typography>
                <Typography sx={{color: 'gray', fontStyle: 'italic'}}>
                  {option.nif || '-'}
                </Typography>
              </Box>
            </li>
          )}
          componentsProps={{
            clearIndicator: {sx: {color: 'white'}},
          }}
          popupIcon={
            <ArrowDropDownIcon sx={{color: selectedUserUuid ? 'white' : 'inherit'}} />
          }
          noOptionsText={t('noResults')}
          filterOptions={(options, {inputValue}) =>
            options.filter((user) =>
              PROPS_INCLUDES_IN_SEARCH.map(prop => user[prop as keyof Profile]).some((field) =>
                String(field ?? '').toLowerCase().includes(inputValue.toLowerCase())
              )
            )
          }
          disablePortal
        />
        }
        {
          availableYears && <SelectInput options={toSelectInputOption(availableYears)}
            onOptionChange={(year) => onYearChange(Number(year))}
            selectedOptionId={year?.toString() || ''} allowEmptyValue={true} placeholderLabel={t('year')}
            sx={sxProps.availableYears} menuSx={menuSx}/>
        }
        {
          municipalities && <SelectInput options={toSelectInputOption(municipalities)}
            onOptionChange={(municipalityUuid) => handleFilterChange(FILTER_KEY.MUNICIPALITY, municipalityUuid)}
            selectedOptionId={filterGroup.municipalityUuid || ''} allowEmptyValue={true} placeholderLabel={t('municipality')}
            sx={sxProps.municipalityUuid} menuSx={menuSx}/>
        }
        { uses && <SelectInput options={toSelectInputOption(uses)}
          onOptionChange={(useUuid) => handleFilterChange(FILTER_KEY.USE, useUuid)}
          selectedOptionId={filterGroup.useUuid || ''} allowEmptyValue={true} placeholderLabel={t('use')}
          sx={sxProps.useUuid} menuSx={menuSx}/>
        }
        {
          crops && <SelectInput options={toSelectInputOption(crops)}
            onOptionChange={(cropUuid) => handleFilterChange(FILTER_KEY.CROP, cropUuid)}
            selectedOptionId={filterGroup.cropUuid || ''} allowEmptyValue={true} placeholderLabel={t('crop')}
            sx={sxProps.cropUuid} menuSx={menuSx}/>
        }
        <SelectInput options={PROTECTION_TYPES}
          onOptionChange={(protection_type) => handleFilterChange(FILTER_KEY.PROTECTION_TYPE, protection_type)}
          selectedOptionId={filterGroup.protectionType || ''} allowEmptyValue={true} placeholderLabel={t('protection')}
          sx={sxProps.protection_type} menuSx={menuSx}/>
        {
          enpTypes && <SelectInput options={toSelectInputOption(enpTypes)}
            onOptionChange={(enpTypes) => handleFilterChange(FILTER_KEY.ENP_TYPE, enpTypes)}
            selectedOptionId={filterGroup.enpType || ''} allowEmptyValue={true}
            placeholderLabel={t('dashboard.enpProtectedAreaTotalInHa')}
            sx={sxProps.enpTypes} menuSx={menuSx}/>
        }
        {
          rnTypes && <SelectInput options={toSelectInputOption(rnTypes)}
            onOptionChange={(rnType) => handleFilterChange(FILTER_KEY.RN_TYPE, rnType)}
            selectedOptionId={filterGroup.rnType || ''} allowEmptyValue={true}
            placeholderLabel={t('dashboard.rnProtectedAreaTotalInHa')}
            sx={sxProps.rnTypes} menuSx={menuSx}/>
        }
        {
          <SelectInput options={MAB_TYPES}
            onOptionChange={(isMab) => handleFilterChange(FILTER_KEY.MAB_TYPE, isMab)}
            selectedOptionId={filterGroup.isMab || ''} allowEmptyValue={true}
            placeholderLabel={t('dashboard.mabProtectedAreaTotalInHa')}
            sx={sxProps.isMab} menuSx={menuSx}/>
        }
      </Box>
    </AccordionDetails>
  </Accordion>;
};

export default FilterPanel;

const genericInputSx: SxProps = {
  '&.SelectInput-root': {//El selector
    width: '100%',
    my: 0.25
  },
};

const containerSx: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0
};

const menuSx: SxProps = {
  zIndex: 9000,
  top: 5,
  left: -5,
  height: '78vh'
};

const accordionSx: SxProps = {
  '&.MuiPaper-root': {
    left: 0
  }
};